import { useEffect, useRef } from 'react';
import { isLeftButton } from '@/utils/event-kit';
import { IPoint } from '@/version-types';
import { PenType } from '../../types';

export const useDrawEvent = ({
    drawPenType,
    disable,
    isDrawEvent,
    convertToLayerPoint,
    onMouseMove,
    onDrawStart,
    onDrawMove,
    onDrawEnd,
}: {
    drawPenType: PenType;
    disable: boolean;
    convertToLayerPoint: (point: IPoint, e: MouseEvent) => IPoint;
    isDrawEvent: (e: MouseEvent) => boolean;
    onMouseMove?: (point: IPoint, e: MouseEvent) => void;
    onDrawStart?: (points: IPoint[]) => void;
    onDrawMove?: (points: IPoint[]) => void;
    onDrawEnd?: (points: IPoint[]) => void;
}) => {
    // 判断当前鼠标事件是否是绘制事件
    const isDrawEventRef = useRef(false);
    // 记录当前画笔的要绘制的所有点
    const drawPointsRef = useRef<IPoint[]>([]);

    useEffect(() => {
        const downHandle = (e) => {
            if (!isLeftButton(e)) return;
            if (disable) return;

            const point = { x: e.x, y: e.y };
            const position = convertToLayerPoint(point, e);

            isDrawEventRef.current = isDrawEvent(e);

            if (!isDrawEventRef.current) return;

            drawPointsRef.current = [position];
            // 涂抹笔，第一笔就需要展示出来
            onDrawStart?.(drawPenType === PenType.BRUSH ? drawPointsRef.current : []);
        };

        const moveHandle = (e) => {
            const point = { x: e.x, y: e.y };
            const position = convertToLayerPoint(point, e);

            onMouseMove?.(position, e);

            if (disable) return;
            if (!isDrawEventRef.current) return;

            if (drawPenType === PenType.BOX) {
                // 矩形绘制复用 path 的方式，需要手动创建四个点
                const startPoint = drawPointsRef.current[0];
                const endPoint = position;

                drawPointsRef.current = [
                    startPoint,
                    { x: startPoint.x, y: endPoint.y },
                    endPoint,
                    { x: endPoint.x, y: startPoint.y },
                ];
            } else {
                drawPointsRef.current.push(position);
            }

            onDrawMove?.(drawPointsRef.current);
        };

        const upHandle = (e) => {
            isDrawEventRef.current = false;

            if (disable) return;
            if (!drawPointsRef.current.length) return;
            // 没有移动鼠标的无效绘制判断
            if (
                (drawPenType === PenType.LASSO && drawPointsRef.current.length < 3) ||
                (drawPenType === PenType.BOX && drawPointsRef.current.length < 4)
            ) {
                drawPointsRef.current = [];
            }

            onDrawEnd?.(drawPointsRef.current);
            drawPointsRef.current = [];
        };

        document.addEventListener('mousemove', moveHandle);
        document.addEventListener('mousedown', downHandle);
        document.addEventListener('mouseup', upHandle);

        return () => {
            document.removeEventListener('mousemove', moveHandle);
            document.removeEventListener('mousedown', downHandle);
            document.removeEventListener('mouseup', upHandle);
        };
    }, [drawPenType, disable, isDrawEvent, convertToLayerPoint, onMouseMove, onDrawStart, onDrawMove, onDrawEnd]);
};
